import { LIFE_AREA_LABELS } from '@astrally/astro-engine';
import { LIFE_AREAS, RASHI_NAMES, type ChartResult, type LifeArea, type Signal } from '@astrally/shared';
import type { ComputedReading } from './horoscopeData';

/**
 * The "Both" view: two traditions side by side, and an honest account of where
 * they part company.
 *
 * Every competitor ships one zodiac and hopes you never notice the other one
 * exists. Showing both immediately raises the question the reader deserves an
 * answer to — "so which one is right?" — and the answer is neither, they are
 * measuring from different origins. Everything in here is DERIVED from the two
 * computed readings and the chart; nothing is asserted, and nothing is an AI
 * call. If the two readings agree this week, this says so.
 *
 * The two real reasons they differ, both structural and both checkable:
 *  1. Different zodiacs. Jyotish measures from the fixed stars, Western from
 *     the March equinox, and precession has pulled those ~24° apart. That is
 *     `chart.meta.ayanamsa.valueDeg` — an actual number in the chart, not a
 *     rounded claim.
 *  2. Different anchor. A Jyotish period reading is counted in signs from the
 *     natal MOON; a Western one tracks transiting planets against the whole
 *     natal chart, where the Sun carries the most weight of any single point.
 */

export interface AreaOverlap {
  area: LifeArea;
  vedic: Signal;
  western: Signal;
}

export interface Divergence {
  /**
   * The reader's Sun in both zodiacs. This is the concrete, personal version of
   * "the two systems disagree" — most people are a different Sun sign in each.
   * Each label speaks its own tradition's language: `sidereal` is a rashi name
   * (Karka…), `tropical` a Western one (Cancer…), so `differs` is computed from
   * the sign INDICES — the two strings never match even when the sign does.
   */
  sun: { sidereal: string; tropical: string; differs: boolean } | null;
  /** The chart's own ayanamsa in degrees — the size of the gap, not a guess. */
  ayanamsaDeg: number;
  /** Areas BOTH traditions flagged this period, strongest signal from each. */
  shared: AreaOverlap[];
  /** Areas only one of them flagged. */
  vedicOnly: LifeArea[];
  westernOnly: LifeArea[];
  vedicTop: Signal | null;
  westernTop: Signal | null;
  /** True when the two loudest signals sit in different parts of life. */
  topAreasDiffer: boolean;
  /** 'agree' when they overlap and lead on the same area; 'diverge' otherwise. */
  verdict: 'agree' | 'overlap' | 'diverge';
}

function strongestIn(reading: ComputedReading, area: LifeArea): Signal | null {
  const list = reading.result.byArea[area];
  if (!list || list.length === 0) return null;
  // byArea preserves the engine's weight-descending order.
  return list[0];
}

export function compareReadings(
  vedic: ComputedReading,
  western: ComputedReading,
  chart: ChartResult,
): Divergence {
  const positions = chart.positions ?? chart.uncertainty?.midpointPositions ?? [];
  const sunPos = positions.find((p) => p.planet === 'sun');
  const sun = sunPos
    ? {
        sidereal: RASHI_NAMES[sunPos.sidereal.sign] ?? sunPos.sidereal.signName,
        tropical: sunPos.tropical.signName,
        differs: sunPos.sidereal.sign !== sunPos.tropical.sign,
      }
    : null;

  const shared: AreaOverlap[] = [];
  const vedicOnly: LifeArea[] = [];
  const westernOnly: LifeArea[] = [];

  for (const area of LIFE_AREAS) {
    const v = strongestIn(vedic, area);
    const w = strongestIn(western, area);
    if (v && w) shared.push({ area, vedic: v, western: w });
    else if (v) vedicOnly.push(area);
    else if (w) westernOnly.push(area);
  }

  const vedicTop = vedic.result.strongest[0] ?? null;
  const westernTop = western.result.strongest[0] ?? null;
  const topAreasDiffer = Boolean(vedicTop && westernTop && vedicTop.area !== westernTop.area);

  const verdict: Divergence['verdict'] =
    vedicTop && westernTop && vedicTop.area === westernTop.area
      ? 'agree'
      : shared.length > 0
        ? 'overlap'
        : 'diverge';

  return {
    sun,
    ayanamsaDeg: chart.meta.ayanamsa.valueDeg,
    shared,
    vedicOnly,
    westernOnly,
    vedicTop,
    westernTop,
    topAreasDiffer,
    verdict,
  };
}

export function areaLabel(area: LifeArea): string {
  return LIFE_AREA_LABELS[area];
}

/** Comma list that ends in "and", for prose rather than a chip row. */
export function joinAreas(areas: LifeArea[]): string {
  const names = areas.map(areaLabel);
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * The one-line WHY. Written here rather than in JSX so the copy can be read,
 * reviewed and tested as a unit — and so the two branches (they agree / they
 * do not) can never drift apart.
 *
 * The `precession` glossary term is linked next to this line by the caller.
 */
export function divergenceLine(d: Divergence): string {
  const gap = `${d.ayanamsaDeg.toFixed(1)}°`;
  if (d.verdict === 'agree') {
    const area = d.vedicTop ? areaLabel(d.vedicTop.area).toLowerCase() : 'the same ground';
    return `This period the two land on the same ground — both are pointing at ${area} — which is worth noticing, because they get there from different starting points: their zodiacs sit ${gap} apart, and Jyotish counts from your Chandra rashi while Western reads the whole birth chart.`;
  }
  const vedicArea = d.vedicTop ? areaLabel(d.vedicTop.area).toLowerCase() : 'a quiet sky';
  const westernArea = d.westernTop ? areaLabel(d.westernTop.area).toLowerCase() : 'a quiet sky';
  return `They point different ways — Jyotish at ${vedicArea}, Western at ${westernArea} — because they are not measuring from the same origin: the two zodiacs sit ${gap} apart after centuries of precession, and Jyotish counts this period from your Chandra rashi — the rashi your Moon held at birth — while Western tracks today's planets against your whole chart, Sun included.`;
}
